import { RepositoryRuntimeVersion } from "@back4app2/sdk";

import TextInput from "../FormInputField/TextInput/TextInput";

// A pin is written into the repository's own version file at build time, so it
// has to be a version and nothing else. Mirrors isValidRuntimeVersionPin on the
// API — the server rejects anything else, and finding that out after submitting
// a whole form is a bad way to learn it.
const PIN_PATTERN = /^\d+(\.\d+){0,2}$/;

export const isValidRuntimePin = (value: string): boolean =>
  PIN_PATTERN.test(value);

interface RuntimeVersionFieldProps {
  // Named for the user: "Node.js", "Java". Absent while the detection runs.
  detectedStack?: string;
  // What the detection found, or nothing while it is still running.
  resolved?: RepositoryRuntimeVersion;
  // The user's pin. Empty means follow the repository, which is the default
  // and the state every app starts in.
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

// What runtime this app will build on, and where that came from.
//
// The field is EMPTY by default and stays that way unless someone types in it.
// Prefilling it with the detected version would look helpful and quietly turn
// every app into a pinned app: the value would then beat the repository, so a
// version added to the repo later would stop taking effect — the exact silent
// override this whole feature exists to remove.
const RuntimeVersionField = ({
  detectedStack,
  resolved,
  value,
  onChange,
  disabled,
}: RuntimeVersionFieldProps) => {
  if (!resolved || !detectedStack) {
    return null;
  }

  const invalid = !!value && !isValidRuntimePin(value);
  const stack = detectedStack;

  let status;

  if (value && !invalid) {
    status = (
      <>
        This app will build on <strong>{stack} {value}</strong>, whatever your
        repository says.
      </>
    );
  } else if (resolved.source === "REPO" && resolved.honored) {
    status = (
      <>
        Your repository asks for <strong>{stack} {resolved.version}</strong> in{" "}
        {resolved.origin}, and the build uses it.
      </>
    );
  } else if (resolved.source === "REPO" && resolved.canPin) {
    // Declared somewhere the buildpack never opens — which the build now
    // repairs by copying the value into the file it does read. Said out loud
    // because the version in the log will not match the file they edited.
    status = (
      <>
        Your repository asks for <strong>{stack} {resolved.version}</strong> in{" "}
        {resolved.origin}. The build reads {resolved.buildpackReads}, so we copy
        it across for you.
      </>
    );
  } else if (resolved.source === "REPO") {
    // Declared, not read, and not something the platform can write either.
    // The only honest thing left is to name the file that would work.
    status = (
      <>
        Your repository asks for <strong>{stack} {resolved.version}</strong> in{" "}
        {resolved.origin}, but the build reads {resolved.buildpackReads} — so
        that version is ignored. Declare it in {resolved.declareIn} to build on
        it.
      </>
    );
  } else if (resolved.version) {
    status = (
      <>
        Your repository doesn't pin a version, so this builds on{" "}
        <strong>
          {stack} {resolved.version}
        </strong>{" "}
        — Back4app's default.
      </>
    );
  } else {
    // No declaration and no platform default: the buildpack picks, and it picks
    // the newest release. Worth saying out loud — it is the one that breaks
    // apps built for an older major.
    status = (
      <>
        Your repository doesn't pin a version, so the build uses the{" "}
        <strong>newest {stack} release</strong>.{" "}
        {resolved.canPin
          ? "Set a version here if your app needs an older one."
          : `Declare it in ${resolved.declareIn} if your app needs an older one.`}
      </>
    );
  }

  // No input where a pin would do nothing — but the label stays. Without it
  // the section is a grey sentence floating between two fields, and the answer
  // to "what runtime am I getting?" reads as missing rather than as given.
  // Same label markup as TextInput, so the two line up in the form.
  if (!resolved.canPin) {
    return (
      <div className="flex flex-col gap-2 w-full">
        <span className="inline-flex items-center justify-start gap-2 font-medium leading-140 capitalize">
          Runtime Version
        </span>
        <div className="text-sm opacity-70">{status}</div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <TextInput
        value={value}
        label="Runtime Version"
        name="runtimeVersion"
        onChange={(e) => onChange(e.target.value)}
        placeholderText={resolved.version || "e.g. 20"}
        infoText="Optional. Leave empty to follow the version your repository declares."
        isError={invalid}
        errorMessage={
          invalid ? 'Use a version number, like "20" or "3.11".' : ""
        }
        isDisabled={disabled}
      />
      <div className="text-sm opacity-70">{status}</div>
    </div>
  );
};

export default RuntimeVersionField;
